import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { cx } from '../../ui';
import styles from './sectionHead.module.css';

/**
 * The head of a foldable section on the watch page: comments, and — below the breakpoint, where
 * they wear the comments header's shape — chapters, the playlist panel and the recommendations rail.
 *
 * Pressing the head toggles the section, and two pieces make that work without giving anything up:
 *
 * - **the heading is a real `<button>`**, so the gesture is keyboard-reachable and announces itself
 *   (`aria-expanded`, with the heading's own words as its name — "Comments 1,234, collapsed, button",
 *   count included, which an `aria-label` would have thrown away);
 * - **the row's empty space presses too**, because "click the header" means the header and not only its
 *   first few words. That is a pointer affordance layered on the button rather than a control of its
 *   own, which is exactly why the button has to exist.
 *
 * `children` are the row's trailing controls — the comments sort chips, and every caller's own
 * Show/Hide. Three controls for one fact sounds like too many, but they are one control (the toggle),
 * one label for it (the word) and one target big enough to hit.
 *
 * The layout classes stay the callers': four sections draw this head and each keeps its own spacing and
 * type. What is shared is the behaviour, the cursor and a button that looks like the heading it is.
 */
export function SectionHead({
  heading,
  shown,
  onToggle,
  children,
  className,
  headingClassName,
  as = 'div',
}: {
  readonly heading: ReactNode;
  readonly shown: boolean;
  readonly onToggle: () => void;
  readonly children?: ReactNode;
  readonly className?: string;
  readonly headingClassName?: string;
  /** `header` for the comments section, which has been one since before it could fold. */
  readonly as?: 'div' | 'header';
}) {
  const Row = as;
  return (
    <Row className={cx(styles.head, className)} onClick={pressRow(onToggle)}>
      <h2 className={headingClassName}>
        <button type="button" className={styles.headingButton} aria-expanded={shown} onClick={onToggle}>
          {heading}
        </button>
      </h2>
      {children}
    </Row>
  );
}

/**
 * A press on the row that did not land on a control of its own — the heading button, a sort chip, the
 * Show/Hide — toggles the section.
 *
 * The guard is what makes the layering safe: without it a press on the heading button would toggle
 * twice (once for the button, once on the way up) and read as a control that does nothing, and a press
 * on a sort chip would fold the list the reader was re-sorting.
 */
function pressRow(onToggle: () => void): (event: ReactMouseEvent<HTMLElement>) => void {
  return (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('button, a, input, select, textarea, label, [role="button"]') !== null) return;
    onToggle();
  };
}
